import { useEffect, useState } from "react";
import { useIsMounted } from "./useIsMounted";

export type SubscribableArrayCallback<T> = (vals: readonly T[]) => void;

export interface SubscribableArraySubscription {
    unsubscribe(): void;
};

export interface SubscribableArray<T> {
    push(val: T): void;
    unshift(val: T): void;
    subscribe(callback: SubscribableArrayCallback<T>): SubscribableArraySubscription;
    getArray(): readonly T[];
}

export function createSubscribableArray<T>(originalValue?: readonly T[]): SubscribableArray<T> {

    let cache: readonly T[] = originalValue || [];

    const subscriptions: SubscribableArrayCallback<T>[] = [];

    function notify() {
        for (const subscription of subscriptions) {
            subscription(cache);
        }
    }

    return {
        getArray: () => cache,
        push(val: T) {
            cache = [...cache, val];
            notify();
        },
        unshift(val: T) {
            cache = [val, ...cache];
            notify();
        },
        subscribe: callback => {
            subscriptions.push(callback);

            return {
                unsubscribe: () => {
                    const index = subscriptions.indexOf(callback);
                    if (index >= 0) {
                        subscriptions.splice(index, 1);
                    }
                }
            };
        }
    };
}

export function useSubscribableArray<T>(array: SubscribableArray<T>): readonly T[] {
    const [res, setRes] = useState<readonly T[]>(array.getArray());

    const mounted = useIsMounted();

    useEffect(() => {
        const subscription = array.subscribe(newVal => {
            if (mounted()) {
                setRes(newVal);
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [mounted, array]);

    return res;
}
